import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  Collapse,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Skeleton,
  Snackbar,
  // Table,       // [Internal Actions — temporarily removed]
  // TableBody,
  // TableCell,
  // TableHead,
  // TableRow,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  WarningAmber as WarningAmberIcon,
  Stars as StarsIcon,
  // Add as AddIcon,  // [Internal Actions — temporarily removed]
  EditNoteOutlined as EditNoteOutlinedIcon,
  ContentCopy as ContentCopyIcon,
  Translate as TranslateIcon,
} from '@mui/icons-material';
import { diffWords } from 'diff';
import { colors, typography, textFieldStyles } from '../../../shared/styles/theme';
import type { ClauseInfo, ClausePosition, CategoryInfo } from '../types/contracts'; // InternalAction — temporarily removed
import { patchClause, deleteClause, postApproveExample, translateClause } from '../utils/contractApi';

const POSITION_OPTIONS: { value: ClausePosition; label: string }[] = [
  { value: 'IDEAL', label: 'Ideal' },
  { value: 'ACCEPTABLE', label: 'Acceptable' },
  { value: 'NON_NEGOTIABLE', label: 'Non-Negotiable' },
  { value: 'UNCLASSIFIED', label: 'Unclassified' },
];

const POSITION_STYLE: Record<ClausePosition, { bg: string; text: string; label: string }> = {
  IDEAL: { bg: '#e8f5e9', text: '#2e7d32', label: 'IDEAL' },
  ACCEPTABLE: { bg: '#e3f2fd', text: '#1565c0', label: 'ACCEPTABLE' },
  NON_NEGOTIABLE: { bg: '#ffebee', text: '#c62828', label: 'NON-NEGOTIABLE' },
  UNCLASSIFIED: { bg: '#f5f5f5', text: '#616161', label: 'UNCLASSIFIED' },
};

const POSITION_CARD_OUTLINE: Record<ClausePosition, { border: string; shadowTint: string }> = {
  IDEAL: {
    border: '1px solid rgba(46, 125, 50, 0.38)',
    shadowTint: '0 12px 28px -8px rgba(46, 125, 50, 0.14)',
  },
  ACCEPTABLE: {
    border: '1px solid rgba(21, 101, 192, 0.38)',
    shadowTint: '0 12px 28px -8px rgba(21, 101, 192, 0.14)',
  },
  NON_NEGOTIABLE: {
    border: '1px solid rgba(198, 40, 40, 0.38)',
    shadowTint: '0 12px 28px -8px rgba(198, 40, 40, 0.13)',
  },
  UNCLASSIFIED: {
    border: '1px solid rgba(97, 97, 97, 0.32)',
    shadowTint: '0 12px 28px -8px rgba(97, 97, 97, 0.11)',
  },
};

// [Internal Actions — temporarily removed]
// const PRIORITY_STYLE: Record<string, { bg: string; text: string }> = {
//   High: { bg: '#ffebee', text: '#c62828' },
//   Medium: { bg: '#fff3e0', text: '#e65100' },
//   Low: { bg: '#e3f2fd', text: '#1565c0' },
// };

const CARD_SHADOW_BASE =
  '0 4px 6px -1px rgba(15, 23, 42, 0.08), 0 2px 4px -2px rgba(15, 23, 42, 0.06), 0 8px 24px -6px rgba(15, 23, 42, 0.06)';

const PREVIEW_LINES = 2;

function PositionBadge({ position }: { position: ClausePosition | null }) {
  if (!position) return null;
  const style = POSITION_STYLE[position] ?? POSITION_STYLE.UNCLASSIFIED;
  return (
    <Chip
      label={style.label}
      size="small"
      sx={{
        backgroundColor: style.bg,
        color: style.text,
        fontWeight: typography.fontWeight.semibold,
        fontSize: typography.fontSize.xs,
      }}
    />
  );
}

/* [Internal Actions — temporarily removed]
function PriorityChip({ priority }: { priority: string }) {
  const style = PRIORITY_STYLE[priority] ?? { bg: '#f5f5f5', text: '#616161' };
  return (
    <Chip
      label={priority}
      size="small"
      sx={{
        backgroundColor: style.bg,
        color: style.text,
        fontWeight: typography.fontWeight.semibold,
        fontSize: typography.fontSize.xs,
        height: 20,
      }}
    />
  );
}

function InternalActionsTable({ actions }: { actions: InternalAction[] }) {
  if (!actions.length) return null;
  return (
    <Table size="small" sx={{ mt: 1.25, borderRadius: 1, overflow: 'hidden', border: `1px solid ${colors.border.light}` }}>
      <TableHead>
        <TableRow sx={{ backgroundColor: 'rgba(248, 250, 252, 0.9)' }}>
          <TableCell sx={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: colors.text.secondary, py: 0.75, px: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Team</TableCell>
          <TableCell sx={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: colors.text.secondary, py: 0.75, px: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Action</TableCell>
          <TableCell sx={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: colors.text.secondary, py: 0.75, px: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em', width: 100 }}>Priority</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {actions.map((a, i) => (
          <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 } }}>
            <TableCell sx={{ fontSize: typography.fontSize.sm, py: 0.75, px: 1.5, color: colors.text.secondary, fontWeight: typography.fontWeight.medium, whiteSpace: 'nowrap' }}>{a.team}</TableCell>
            <TableCell sx={{ fontSize: typography.fontSize.sm, py: 0.75, px: 1.5, color: colors.text.primary, lineHeight: 1.5 }}>{a.action}</TableCell>
            <TableCell sx={{ py: 0.75, px: 1.5 }}><PriorityChip priority={a.priority} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function emptyAction(): InternalAction {
  return { team: '', action: '', priority: 'Medium' };
}

interface InternalActionsEditorProps {
  actions: InternalAction[];
  onChange: (actions: InternalAction[]) => void;
}

function InternalActionsEditor({ actions, onChange }: InternalActionsEditorProps) {
  const update = (i: number, field: keyof InternalAction, value: string) => {
    const next = actions.map((a, idx) => (idx === i ? { ...a, [field]: value } : a));
    onChange(next);
  };
  const remove = (i: number) => onChange(actions.filter((_, idx) => idx !== i));
  const add = () => onChange([...actions, emptyAction()]);

  return (
    <Box>
      {actions.map((a, i) => (
        <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr 120px 36px', gap: 1, mb: 1, alignItems: 'flex-start' }}>
          <TextField
            size="small"
            placeholder="Team"
            value={a.team}
            onChange={(e) => update(i, 'team', e.target.value)}
            sx={textFieldStyles}
          />
          <TextField
            size="small"
            placeholder="Action"
            value={a.action}
            onChange={(e) => update(i, 'action', e.target.value)}
            sx={textFieldStyles}
          />
          <TextField
            select
            size="small"
            value={a.priority}
            onChange={(e) => update(i, 'priority', e.target.value)}
            sx={textFieldStyles}
          >
            {['High', 'Medium', 'Low'].map((p) => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </TextField>
          <IconButton size="small" onClick={() => remove(i)} sx={{ color: colors.text.secondary, mt: 0.25 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={add}
        sx={{ textTransform: 'none', color: colors.text.secondary, fontSize: typography.fontSize.xs, mt: 0.5 }}
      >
        Add action
      </Button>
    </Box>
  );
}
*/

interface ClauseCardProps {
  clause: ClauseInfo;
  index: number;
  isSigned?: boolean;
  isAssessing?: boolean;
  categories?: CategoryInfo[];
  onClauseUpdated?: (updated: ClauseInfo) => void;
  onClauseDeleted?: (clauseUuid: string) => void;
}

const ClauseCard: React.FC<ClauseCardProps> = ({ clause, index, isSigned = false, isAssessing = false, categories = [], onClauseUpdated, onClauseDeleted }) => {
  const [legalExpanded, setLegalExpanded] = useState(
    // Auto-expand when a suggested modification exists so the reader can
    // compare the original and the diff side-by-side without an extra click.
    !!clause.suggested_modification
  );
  const [editOpen, setEditOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Translation state
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  const [translatedModification, setTranslatedModification] = useState<string | null>(null);
  const [detectedLang, setDetectedLang] = useState<string | null>(null);
  const [showTranslation, setShowTranslation] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState<string | null>(null);

  const handleTranslateToggle = useCallback(async () => {
    if (showTranslation) {
      setShowTranslation(false);
      return;
    }
    if (translatedText !== null) {
      // Already fetched — just toggle
      setShowTranslation(true);
      return;
    }
    setTranslating(true);
    setTranslateError(null);
    try {
      const result = await translateClause(clause.clause_uuid);
      setTranslatedText(result.translated_text);
      setTranslatedModification(result.translated_modification ?? null);
      setDetectedLang(result.detected_language);
      setShowTranslation(true);
    } catch {
      setTranslateError('Translation failed. Please try again.');
    } finally {
      setTranslating(false);
    }
  }, [clause.clause_uuid, showTranslation, translatedText]);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    edited_original_text: clause.edited_original_text ?? clause.original_text ?? '',
    human_category_tag: clause.category_tag ?? '',
    human_position: clause.human_position ?? null as ClausePosition | null,
    human_tw_response: clause.human_tw_response ?? '',
  });

  const [approveOpen, setApproveOpen] = useState(false);
  const [approveSaving, setApproveSaving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [approveForm, setApproveForm] = useState({
    position: clause.position ?? null as ClausePosition | null,
    tw_response: clause.tw_response ?? '',
    suggested_modification: clause.suggested_modification ?? '',
    // internal_actions: clause.internal_actions ?? [] as InternalAction[], // [Internal Actions — temporarily removed]
  });
  const [redlineCopied, setRedlineCopied] = useState(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');

  // True once AI assessment has run on this clause (category_tag is assigned in stage 3+)
  const isAssessed = clause.category_tag != null;
  // Approved clauses are locked — only "Approve as Example" sets reviewed_by
  const isApproved = clause.reviewed_by != null;

  const openEdit = useCallback(() => {
    setEditForm({
      edited_original_text: clause.edited_original_text ?? clause.original_text ?? '',
      human_category_tag: clause.category_tag ?? '',
      human_position: clause.human_position ?? null,
      human_tw_response: clause.human_tw_response ?? '',
    });
    setEditError(null);
    setEditOpen(true);
  }, [clause]);

  const closeEdit = useCallback(() => {
    if (!saving) setEditOpen(false);
  }, [saving]);

  const handleSaveEdit = useCallback(async () => {
    setSaving(true);
    setEditError(null);
    try {
      // Determine the current effective text so we only send edited_original_text
      // when it has actually changed (avoids unnecessary AI assessment resets).
      const currentEffective = (clause.edited_original_text ?? clause.original_text ?? '').trim();
      const newText = editForm.edited_original_text.trim();
      const categoryChanged =
        editForm.human_category_tag !== '' &&
        editForm.human_category_tag !== (clause.category_tag ?? '');
      const res = await patchClause(clause.clause_uuid, {
        edited_original_text: newText !== currentEffective ? newText : undefined,
        // Only send human review fields when AI assessment has already run
        ...(isAssessed && {
          ...(categoryChanged && { category_tag: editForm.human_category_tag }),
          human_position: editForm.human_position,
          human_tw_response: editForm.human_tw_response || null,
        }),
      });
      onClauseUpdated?.(res.clause);
      setEditOpen(false);
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'response' in e
        ? (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
        : null;
      setEditError(typeof msg === 'string' ? msg : 'Failed to update clause.');
    } finally {
      setSaving(false);
    }
  }, [clause.clause_uuid, clause.edited_original_text, clause.original_text, clause.category_tag, editForm, isAssessed, onClauseUpdated]);

  const openApprove = useCallback(() => {
    setApproveForm({
      position: clause.position ?? null,
      tw_response: clause.tw_response ?? '',
      suggested_modification: clause.suggested_modification ?? '',
      // internal_actions: clause.internal_actions ?? [], // [Internal Actions — temporarily removed]
    });
    setApproveError(null);
    setApproveOpen(true);
  }, [clause]);

  const closeApprove = useCallback(() => {
    if (!approveSaving) setApproveOpen(false);
  }, [approveSaving]);

  const handleSaveApprove = useCallback(async () => {
    setApproveSaving(true);
    setApproveError(null);
    try {
      const res = await postApproveExample(clause.clause_uuid, {
        position: approveForm.position ?? undefined,
        tw_response: approveForm.tw_response || undefined,
        suggested_modification: approveForm.suggested_modification || undefined,
        // internal_actions: approveForm.internal_actions.length ? approveForm.internal_actions : undefined, // [Internal Actions — temporarily removed]
      });
      onClauseUpdated?.(res.clause);
      setApproveOpen(false);
      setSnackMessage('Example saved — this will improve future assessments.');
      setSnackOpen(true);
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'response' in e
        ? (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
        : null;
      setApproveError(typeof msg === 'string' ? msg : 'Failed to save example.');
    } finally {
      setApproveSaving(false);
    }
  }, [clause.clause_uuid, approveForm, onClauseUpdated]);

  const handleDeleteClick = useCallback(() => {
    setDeleteError(null);
    setDeleteConfirmOpen(true);
  }, []);

  const closeDeleteConfirm = useCallback(() => {
    if (!deleting) setDeleteConfirmOpen(false);
  }, [deleting]);

  const handleConfirmDelete = useCallback(async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteClause(clause.clause_uuid);
      onClauseDeleted?.(clause.clause_uuid);
      setDeleteConfirmOpen(false);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : null;
      setDeleteError(typeof msg === 'string' ? msg : 'Failed to delete clause.');
    } finally {
      setDeleting(false);
    }
  }, [clause.clause_uuid, onClauseDeleted]);

  // Use edited text when present so card body and diff always reflect the latest content
  const original = clause.edited_original_text ?? clause.original_text ?? '';
  const effectivePosition = clause.human_position ?? clause.position;

  const hasAssessment = useMemo(
    () =>
      effectivePosition != null ||
      clause.tw_response != null ||
      clause.suggested_modification != null ||
      // (clause.internal_actions && clause.internal_actions.length > 0) || // [Internal Actions — temporarily removed]
      clause.human_tw_response != null,
      // (clause.human_internal_actions && clause.human_internal_actions.length > 0) // [Internal Actions — temporarily removed]
    [clause, effectivePosition]
  );

  const canApprove = clause.position != null && clause.category_tag != null && !clause.reviewed_by;

  const clientCommentMax = 500;
  const clientCommentCount = editForm.human_tw_response.length;

  const outline = effectivePosition ? POSITION_CARD_OUTLINE[effectivePosition] : null;
  const cardBorder = outline?.border ?? `1px solid ${colors.border.light}`;
  const cardShadow = outline ? `${CARD_SHADOW_BASE}, ${outline.shadowTint}` : CARD_SHADOW_BASE;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          backgroundColor: colors.background.card,
          border: cardBorder,
          borderRadius: 1.5,
          boxShadow: cardShadow,
          mb: 2,
        }}
      >
        {/* TOP ROW: Section title + metadata chips */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: typography.fontFamily.heading,
                fontWeight: typography.fontWeight.bold,
                color: colors.text.primary,
                fontSize: typography.fontSize.md,
              }}
            >
              {clause.section_name || `Clause ${index + 1}`}
            </Typography>
            {clause.category_tag && (
              <Tooltip title={clause.category_tag}>
                <Chip
                  label={clause.category_name ?? clause.category_tag}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: colors.border.medium,
                    color: colors.text.secondary,
                    fontSize: typography.fontSize.xs,
                    fontWeight: typography.fontWeight.medium,
                    letterSpacing: '0.04em',
                  }}
                />
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {clause.billable_flag && (
              <Tooltip title="This clause triggers a billable security service line item">
                <Chip
                  icon={<WarningAmberIcon sx={{ fontSize: '0.85rem !important' }} />}
                  label="BILLABLE SERVICE"
                  size="small"
                  sx={{
                    backgroundColor: '#fff8e1',
                    color: '#e65100',
                    fontWeight: typography.fontWeight.semibold,
                    fontSize: typography.fontSize.xs,
                    border: '1px solid rgba(230, 81, 0, 0.3)',
                    '& .MuiChip-icon': { color: '#e65100' },
                  }}
                />
              </Tooltip>
            )}
            {clause.is_manual && (
              <Chip
                label="Manually added"
                size="small"
                variant="outlined"
                sx={{
                  borderColor: colors.primary,
                  color: colors.primary,
                  fontSize: typography.fontSize.xs,
                }}
              />
            )}
            {clause.reviewed_by && (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: '0.85rem !important' }} />}
                label="Approved"
                size="small"
                sx={{
                  backgroundColor: '#e8f5e9',
                  color: '#2e7d32',
                  fontWeight: typography.fontWeight.semibold,
                  fontSize: typography.fontSize.xs,
                  border: '1px solid rgba(46, 125, 50, 0.3)',
                  '& .MuiChip-icon': { color: '#2e7d32' },
                }}
              />
            )}
            {clause.edited_original_text && (
              <Tooltip title="Original text was edited — AI will re-assess on next run">
                <Chip
                  icon={<EditNoteOutlinedIcon sx={{ fontSize: '0.85rem !important' }} />}
                  label="Text edited"
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: 'rgba(21, 108, 134, 0.45)',
                    color: colors.primary,
                    fontSize: typography.fontSize.xs,
                    '& .MuiChip-icon': { color: colors.primary },
                  }}
                />
              </Tooltip>
            )}
            <PositionBadge position={effectivePosition} />
            {!effectivePosition && (
              <Chip
                label="Not assessed"
                size="small"
                variant="outlined"
                sx={{
                  borderColor: colors.border.medium,
                  color: colors.text.disabled,
                  fontSize: typography.fontSize.xs,
                }}
              />
            )}
            {/* Card-level translate toggle — one click translates both panels */}
            {showTranslation ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<TranslateIcon sx={{ fontSize: '0.85rem !important' }} />}
                onClick={handleTranslateToggle}
                sx={{
                  borderColor: colors.primary,
                  color: colors.primary,
                  backgroundColor: 'rgba(21, 108, 134, 0.06)',
                  textTransform: 'none',
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.semibold,
                  px: 1.25,
                  height: 26,
                  borderRadius: '20px',
                  '&:hover': {
                    backgroundColor: 'rgba(21, 108, 134, 0.14)',
                    borderColor: colors.primaryHover,
                  },
                }}
              >
                Viewing in English
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  translating
                    ? <TranslateIcon sx={{ fontSize: '0.85rem !important', opacity: 0.5 }} />
                    : <TranslateIcon sx={{ fontSize: '0.85rem !important' }} />
                }
                onClick={handleTranslateToggle}
                disabled={translating}
                sx={{
                  borderColor: colors.border.medium,
                  color: colors.text.secondary,
                  textTransform: 'none',
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.medium,
                  px: 1.25,
                  height: 26,
                  borderRadius: '20px',
                  '&:hover': {
                    borderColor: colors.primary,
                    color: colors.primary,
                    backgroundColor: 'rgba(21, 108, 134, 0.04)',
                  },
                }}
              >
                {translating ? 'Translating…' : 'Translate'}
              </Button>
            )}
          </Box>
        </Box>

        {/* PRIMARY BLOCK: Simplified Requirement */}
        {clause.simplified_requirement && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              color: colors.text.secondary,
              fontSize: typography.fontSize.sm,
              lineHeight: 1.6,
              wordBreak: 'break-word',
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.semibold,
                color: colors.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                mr: 0.75,
              }}
            >
              Simplified Requirement:
            </Box>
            <Box component="span" sx={{ color: colors.text.primary, fontWeight: typography.fontWeight.medium }}>
              {clause.simplified_requirement}
            </Box>
          </Typography>
        )}

        {/* MISMATCH FLAGS */}
        {clause.mismatch_flags && clause.mismatch_flags.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {clause.mismatch_flags.map((flag, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  backgroundColor: '#fff8e1',
                  border: '1px solid rgba(230, 81, 0, 0.25)',
                }}
              >
                <WarningAmberIcon sx={{ color: '#e65100', fontSize: 16, mt: 0.1, flexShrink: 0 }} />
                <Typography sx={{ fontSize: typography.fontSize.xs, color: '#5d2e00', lineHeight: 1.5 }}>
                  <Box component="span" sx={{ fontWeight: typography.fontWeight.semibold }}>{flag.type}: </Box>
                  {flag.detail}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {(hasAssessment || original.trim()) && (
          <Box
            sx={{
              mb: 2,
              borderRadius: 1.25,
              border: `1px solid ${colors.border.light}`,
              overflow: 'hidden',
              backgroundColor: '#fff',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 0,
              }}
            >
              {hasAssessment && (
                <Box
                  sx={{
                    flex: '1 1 0',
                    p: 2,
                    borderRight: { xs: 'none', md: `1px solid ${colors.border.light}` },
                    borderBottom: { xs: `1px solid ${colors.border.light}`, md: 'none' },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontSize: typography.fontSize.xs,
                      fontWeight: typography.fontWeight.semibold,
                      color: colors.text.secondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      mb: 1.5,
                    }}
                  >
                    AI Assessment
                  </Typography>

                  {!isSigned && clause.position === 'NON_NEGOTIABLE' && clause.suggested_modification && (
                    <Box sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <EditNoteOutlinedIcon sx={{ fontSize: 14, color: colors.primaryLight }} />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: typography.fontSize.xs,
                              fontWeight: typography.fontWeight.semibold,
                              color: colors.primaryLight,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                            }}
                          >
                            Suggested Modification
                          </Typography>
                        </Box>
                        <Tooltip title={redlineCopied ? 'Copied!' : 'Copy original modification text'}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(clause.suggested_modification!);
                              setRedlineCopied(true);
                              setTimeout(() => setRedlineCopied(false), 2000);
                            }}
                            sx={{ color: redlineCopied ? '#2e7d32' : colors.text.secondary, p: 0.5 }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Box
                        sx={{
                          borderLeft: `3px solid ${colors.primaryLight}`,
                          backgroundColor: 'rgba(72, 161, 173, 0.08)',
                          borderRadius: '0 6px 6px 0',
                          px: 1.5,
                          py: 1.25,
                          fontFamily: 'monospace',
                          fontSize: typography.fontSize.sm,
                          lineHeight: 1.7,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {(() => {
                          // When translation is active and available, diff the translated pair
                          const diffBase = showTranslation && translatedText
                            ? translatedText
                            : (clause.edited_original_text ?? clause.original_text);
                          const diffTarget = showTranslation && translatedModification
                            ? translatedModification
                            : clause.suggested_modification!;
                          return diffBase
                            ? diffWords(diffBase, diffTarget).map((part, i) =>
                                part.added ? (
                                  <span key={i} style={{ color: '#1b5e20', textDecoration: 'underline', backgroundColor: 'rgba(46,125,50,0.10)' }}>{part.value}</span>
                                ) : part.removed ? (
                                  <span key={i} style={{ color: '#b71c1c', textDecoration: 'line-through', opacity: 0.7 }}>{part.value}</span>
                                ) : (
                                  <span key={i} style={{ color: '#37474f' }}>{part.value}</span>
                                )
                              )
                            : <span style={{ color: '#37474f', fontStyle: 'italic' }}>{diffTarget}</span>;
                        })()}
                      </Box>
                    </Box>
                  )}

                  {clause.tw_response && (
                    <Box sx={{ mt: (!isSigned && clause.suggested_modification) ? 1.5 : 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.semibold,
                          color: colors.text.secondary,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          mb: 0.5,
                        }}
                      >
                        TW Response
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.text.primary,
                          fontSize: typography.fontSize.sm,
                          lineHeight: 1.6,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {clause.tw_response}
                      </Typography>
                    </Box>
                  )}

                  {/* [Internal Actions — temporarily removed]
                  {clause.internal_actions && clause.internal_actions.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.semibold,
                          color: colors.text.secondary,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          mb: 0.5,
                        }}
                      >
                        Internal Actions
                      </Typography>
                      <InternalActionsTable actions={clause.internal_actions} />
                    </Box>
                  )}
                  */}

                  {clause.human_tw_response && (
                    <Box sx={{ mt: 1.75, pt: 1.5, borderTop: `1px solid ${colors.border.light}` }}>
                      {clause.human_tw_response && (
                        <Box sx={{ mb: 0 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              color: colors.text.secondary,
                              fontWeight: typography.fontWeight.semibold,
                              fontSize: typography.fontSize.xs,
                              mb: 0.5,
                            }}
                          >
                            Human TW Response
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: colors.text.primary,
                              fontSize: typography.fontSize.sm,
                              lineHeight: 1.5,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {clause.human_tw_response}
                          </Typography>
                        </Box>
                      )}
                      {/* [Internal Actions — temporarily removed]
                      {clause.human_internal_actions && clause.human_internal_actions.length > 0 && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              color: colors.text.secondary,
                              fontWeight: typography.fontWeight.semibold,
                              fontSize: typography.fontSize.xs,
                              mb: 0.5,
                            }}
                          >
                            Human actions
                          </Typography>
                          <InternalActionsTable actions={clause.human_internal_actions} />
                        </Box>
                      )}
                      */}
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={{ flex: '1 1 0', p: 2 }}>
                {/* Clean header — translate lives at card level */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontSize: typography.fontSize.xs,
                      fontWeight: typography.fontWeight.semibold,
                      color: colors.text.secondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Original Contract Text
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setLegalExpanded((p) => !p)}
                    endIcon={legalExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{
                      textTransform: 'none',
                      color: colors.text.secondary,
                      fontWeight: typography.fontWeight.medium,
                      fontSize: typography.fontSize.xs,
                      px: 1,
                      minHeight: 28,
                      '&:hover': { backgroundColor: colors.background.hover },
                    }}
                  >
                    {legalExpanded ? 'Hide' : 'Show'}
                  </Button>
                </Box>

                {translateError && (
                  <Alert severity="error" onClose={() => setTranslateError(null)} sx={{ mb: 1, py: 0, fontSize: typography.fontSize.xs }}>
                    {translateError}
                  </Alert>
                )}

                <Collapse in={legalExpanded} collapsedSize="3.8em">
                  {translating ? (
                    <Box>
                      <Skeleton variant="text" width="100%" height={18} sx={{ mb: 0.5 }} />
                      <Skeleton variant="text" width="92%" height={18} sx={{ mb: 0.5 }} />
                      <Skeleton variant="text" width="80%" height={18} />
                    </Box>
                  ) : (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.text.secondary,
                          fontSize: typography.fontSize.sm,
                          fontWeight: typography.fontWeight.regular,
                          lineHeight: typography.lineHeight.relaxed,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          display: '-webkit-box',
                          WebkitLineClamp: legalExpanded ? 'unset' : PREVIEW_LINES,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {showTranslation && translatedText !== null ? translatedText : original}
                      </Typography>
                      {/* Browser-style machine translation disclaimer */}
                      {showTranslation && translatedText !== null && detectedLang && detectedLang !== 'und' && (
                        <Typography sx={{ fontSize: '0.68rem', color: colors.text.disabled, mt: 0.75, display: 'flex', alignItems: 'center' }}>
                          Machine translated from {detectedLang.toUpperCase()}
                          &nbsp;·&nbsp;
                          <Box
                            component="span"
                            onClick={() => setShowTranslation(false)}
                            sx={{ cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: colors.text.secondary } }}
                          >
                            View original
                          </Box>
                        </Typography>
                      )}
                    </>
                  )}
                </Collapse>
              </Box>
            </Box>
          </Box>
        )}

        {/* REVIEWER ACTIONS */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mt: 2,
            pt: 1.5,
            borderTop: `1px solid ${colors.border.light}`,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {!isApproved && (
              <Button
                variant="outlined"
                onClick={openEdit}
                disabled={isAssessing}
                startIcon={<EditIcon />}
                sx={{
                  borderColor: colors.border.medium,
                  color: colors.text.primary,
                  textTransform: 'none',
                  '&:hover': { borderColor: colors.primaryHover, backgroundColor: 'rgba(21, 108, 134, 0.04)' },
                }}
              >
                Edit Clause
              </Button>
            )}
            {canApprove && (
              <Button
                variant="outlined"
                onClick={openApprove}
                startIcon={<StarsIcon />}
                sx={{
                  borderColor: 'rgba(46, 125, 50, 0.5)',
                  color: '#2e7d32',
                  textTransform: 'none',
                  '&:hover': { borderColor: '#2e7d32', backgroundColor: 'rgba(46, 125, 50, 0.04)' },
                }}
              >
                Approve as Example
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {onClauseDeleted && !isApproved && (
              <Tooltip title="Delete clause">
                <IconButton
                  size="small"
                  sx={{ color: colors.text.secondary }}
                  aria-label="Delete clause"
                  onClick={handleDeleteClick}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Paper>

      {/* EDIT CLAUSE DIALOG */}
      <Dialog open={editOpen} onClose={closeEdit} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 3,
            py: 2,
            borderBottom: `1px solid ${colors.border.light}`,
            backgroundColor: '#ffffff',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(21, 108, 134, 0.10)',
                color: colors.primary,
                flexShrink: 0,
              }}
            >
              <EditIcon fontSize="small" />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.heading,
                fontWeight: typography.fontWeight.bold,
                color: colors.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Edit Clause
            </Typography>
          </Box>
          <IconButton onClick={closeEdit} disabled={saving} aria-label="Close" sx={{ color: colors.text.secondary }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: colors.background.page }}>
          {editError && (
            <Alert severity="error" onClose={() => setEditError(null)} sx={{ m: 3, mb: 0 }}>
              {editError}
            </Alert>
          )}
          <Box sx={{ px: 3, py: 3 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 3,
              }}
            >
              {/* Left panel: Original clause text */}
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${colors.border.light}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: '#fff',
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    borderBottom: `1px solid ${colors.border.light}`,
                    backgroundColor: 'rgba(248, 250, 252, 0.9)',
                  }}
                >
                  <DescriptionOutlinedIcon sx={{ color: colors.text.secondary }} fontSize="small" />
                  <Typography
                    sx={{
                      fontSize: typography.fontSize.sm,
                      fontWeight: typography.fontWeight.semibold,
                      color: colors.text.secondary,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Original Clause Text
                  </Typography>
                </Box>
                <Box sx={{ p: 2.5 }}>
                  <Box
                    sx={{
                      border: `1px solid ${colors.border.light}`,
                      borderRadius: 2,
                      backgroundColor: '#fff',
                      p: 2.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: typography.fontSize.xs,
                        fontWeight: typography.fontWeight.semibold,
                        color: colors.text.secondary,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        mb: 0.75,
                      }}
                    >
                      Source Context
                    </Typography>
                    <Typography sx={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.text.primary, mb: 1 }}>
                      {clause.section_name || `Clause ${index + 1}`}
                    </Typography>

                    {clause.simplified_requirement && (
                      <>
                        <Typography
                          sx={{
                            fontSize: typography.fontSize.xs,
                            fontWeight: typography.fontWeight.semibold,
                            color: colors.text.secondary,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            mt: 2,
                            mb: 0.75,
                          }}
                        >
                          Simplified Requirement
                        </Typography>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: `1px solid ${colors.border.light}`,
                            backgroundColor: 'rgba(248, 250, 252, 0.7)',
                          }}
                        >
                          <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.text.primary, lineHeight: 1.6 }}>
                            {clause.simplified_requirement}
                          </Typography>
                        </Box>
                      </>
                    )}

                    <Typography
                      sx={{
                        fontSize: typography.fontSize.xs,
                        fontWeight: typography.fontWeight.semibold,
                        color: colors.text.secondary,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        mt: 2,
                        mb: 0.75,
                      }}
                    >
                      Full Extracted Text
                    </Typography>
                    <Box
                      sx={{
                        maxHeight: { xs: 260, md: 360 },
                        overflowY: 'auto',
                        pr: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: typography.fontSize.sm,
                          color: colors.text.secondary,
                          lineHeight: 1.7,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {clause.original_text || '—'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>

              {/* Right panel: Edit clause text */}
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${colors.border.light}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: '#fff',
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    borderBottom: `1px solid ${colors.border.light}`,
                    backgroundColor: 'rgba(248, 250, 252, 0.9)',
                  }}
                >
                  <EditNoteOutlinedIcon sx={{ color: colors.text.secondary }} fontSize="small" />
                  <Typography
                    sx={{
                      fontSize: typography.fontSize.sm,
                      fontWeight: typography.fontWeight.semibold,
                      color: colors.text.secondary,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Edit Clause Text
                  </Typography>
                </Box>
                <Box sx={{ p: 2.5 }}>
                  {/* Position and Client Comment — only shown after AI assessment (stage 3+) */}
                  {isAssessed && (
                    <>
                      <Typography sx={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.text.primary, mb: 1 }}>
                        Category
                      </Typography>
                      {categories.length > 0 ? (
                        <TextField
                          select
                          fullWidth
                          value={editForm.human_category_tag}
                          onChange={(e) => setEditForm((f) => ({ ...f, human_category_tag: e.target.value }))}
                          sx={{ mb: 2.5, ...textFieldStyles }}
                        >
                          {categories.map((cat) => (
                            <MenuItem key={cat.tag} value={cat.tag}>
                              {cat.category_name} ({cat.tag})
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.text.secondary, mb: 2.5, px: 1.5, py: 1, border: `1px solid ${colors.border.light}`, borderRadius: 1 }}>
                          {clause.category_name ?? clause.category_tag ?? '—'}
                        </Typography>
                      )}

                      <Typography sx={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.text.primary, mb: 1 }}>
                        Position
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        value={editForm.human_position ?? ''}
                        onChange={(e) => setEditForm((f) => ({ ...f, human_position: (e.target.value || null) as ClausePosition | null }))}
                        sx={{ mb: 2.5, ...textFieldStyles }}
                      >
                        <MenuItem value="">— (use AI assessment)</MenuItem>
                        {POSITION_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>

                      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                        <Typography sx={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.text.primary }}>
                          Client Comment
                        </Typography>
                        <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.text.disabled, fontWeight: typography.fontWeight.medium }}>
                          {clientCommentCount} / {clientCommentMax}
                        </Typography>
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        value={editForm.human_tw_response}
                        onChange={(e) => setEditForm((f) => ({ ...f, human_tw_response: e.target.value.slice(0, clientCommentMax) }))}
                        placeholder="Redline comment ready to paste into the client response…"
                        inputProps={{ maxLength: clientCommentMax }}
                        sx={{ mb: 2.5, ...textFieldStyles }}
                      />

                      <Box sx={{ borderTop: `1px solid ${colors.border.light}`, mb: 2.5 }} />
                    </>
                  )}

                  {!isAssessed && (
                    <>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                    <Typography sx={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.text.primary }}>
                      Clause Text
                    </Typography>
                    <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.text.disabled }}>
                      {editForm.edited_original_text.length} chars
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    minRows={8}
                    maxRows={16}
                    value={editForm.edited_original_text}
                    onChange={(e) => setEditForm((f) => ({ ...f, edited_original_text: e.target.value }))}
                    placeholder="Clause text…"
                    sx={{
                      ...textFieldStyles,
                      '& .MuiInputBase-input': {
                        fontSize: typography.fontSize.sm,
                        lineHeight: 1.7,
                        fontFamily: 'inherit',
                      },
                    }}
                  />
                  {clause.edited_original_text && clause.edited_original_text !== clause.original_text && (
                    <Typography
                      sx={{
                        mt: 0.75,
                        fontSize: typography.fontSize.xs,
                        color: colors.text.disabled,
                        fontStyle: 'italic',
                        lineHeight: 1.5,
                      }}
                    >
                      Previously edited — original extraction: {clause.original_text}
                    </Typography>
                  )}
                    </>
                  )}
                </Box>
              </Paper>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${colors.border.light}`,
            backgroundColor: '#ffffff',
          }}
        >
          <Button onClick={closeEdit} disabled={saving} sx={{ textTransform: 'none', color: colors.text.secondary }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={saving}
            startIcon={!saving ? <CheckCircleIcon /> : undefined}
            sx={{
              textTransform: 'none',
              backgroundColor: colors.primary,
              '&:hover': { backgroundColor: colors.primaryHover },
            }}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* APPROVE AS EXAMPLE DIALOG */}
      <Dialog open={approveOpen} onClose={closeApprove} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 3,
            py: 2,
            borderBottom: `1px solid ${colors.border.light}`,
            backgroundColor: '#ffffff',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(46, 125, 50, 0.10)',
                color: '#2e7d32',
                flexShrink: 0,
              }}
            >
              <StarsIcon fontSize="small" />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.heading,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.text.primary,
                  lineHeight: 1.2,
                }}
              >
                Save as Training Example
              </Typography>
              {clause.category_tag && (
                <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                  Category: {clause.category_tag}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={closeApprove} disabled={approveSaving} aria-label="Close" sx={{ color: colors.text.secondary }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1, backgroundColor: colors.background.page }}>
          {approveError && (
            <Alert severity="error" onClose={() => setApproveError(null)} sx={{ mb: 2 }}>
              {approveError}
            </Alert>
          )}

          <Typography sx={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.text.primary, mb: 1 }}>
            Position
          </Typography>
          <TextField
            select
            fullWidth
            value={approveForm.position ?? ''}
            onChange={(e) => setApproveForm((f) => ({ ...f, position: (e.target.value || null) as ClausePosition | null }))}
            sx={{ mb: 2.5, ...textFieldStyles }}
          >
            <MenuItem value="">—</MenuItem>
            {POSITION_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <Typography sx={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.text.primary, mb: 1 }}>
            Suggested Modification
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={approveForm.suggested_modification}
            onChange={(e) => setApproveForm((f) => ({ ...f, suggested_modification: e.target.value }))}
            placeholder="Exact rewritten clause text, ready to paste as a redline…"
            sx={{ mb: 2.5, ...textFieldStyles }}
          />

          <Typography sx={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.text.primary, mb: 1 }}>
            Client Comment
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={approveForm.tw_response}
            onChange={(e) => setApproveForm((f) => ({ ...f, tw_response: e.target.value }))}
            placeholder="Ready-to-paste redline comment…"
            sx={{ mb: 2.5, ...textFieldStyles }}
          />

          {/* [Internal Actions — temporarily removed]
          <Typography sx={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.text.primary, mb: 1 }}>
            Internal Actions
          </Typography>
          <InternalActionsEditor
            actions={approveForm.internal_actions}
            onChange={(actions) => setApproveForm((f) => ({ ...f, internal_actions: actions }))}
          />
          */}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${colors.border.light}`,
            backgroundColor: '#ffffff',
          }}
        >
          <Button onClick={closeApprove} disabled={approveSaving} sx={{ textTransform: 'none', color: colors.text.secondary }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveApprove}
            disabled={approveSaving}
            startIcon={!approveSaving ? <StarsIcon /> : undefined}
            sx={{
              textTransform: 'none',
              backgroundColor: '#2e7d32',
              '&:hover': { backgroundColor: '#1b5e20' },
            }}
          >
            {approveSaving ? 'Saving…' : 'Save to Flywheel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE CONFIRM DIALOG */}
      <Dialog open={deleteConfirmOpen} onClose={closeDeleteConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>Delete clause?</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" onClose={() => setDeleteError(null)} sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            This clause will be removed from the session. You can add it again later with &quot;Add clause&quot; if needed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteConfirm} disabled={deleting} sx={{ color: colors.text.secondary }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SUCCESS SNACKBAR */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackOpen(false)} severity="success" sx={{ width: '100%' }}>
          {snackMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

const skeletonBg = 'rgba(0, 0, 0, 0.06)';

/** Skeleton placeholder for a clause card while loading. */
export const ClauseCardSkeleton: React.FC = () => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      backgroundColor: colors.background.card,
      border: `1px solid ${colors.border.light}`,
      borderRadius: 1.5,
      boxShadow: CARD_SHADOW_BASE,
      mb: 2,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
      <Skeleton variant="text" width={160} height={24} sx={{ bgcolor: skeletonBg }} />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Skeleton variant="rounded" width={56} height={24} sx={{ bgcolor: skeletonBg }} />
        <Skeleton variant="rounded" width={90} height={24} sx={{ bgcolor: skeletonBg }} />
      </Box>
    </Box>
    <Box sx={{ mb: 2 }}>
      <Skeleton variant="text" width="40%" height={14} sx={{ mb: 1, bgcolor: skeletonBg }} />
      <Skeleton variant="text" width="100%" height={20} sx={{ mb: 0.75, bgcolor: skeletonBg }} />
      <Skeleton variant="text" width="95%" height={20} sx={{ mb: 0.75, bgcolor: skeletonBg }} />
      <Skeleton variant="text" width="88%" height={20} sx={{ bgcolor: skeletonBg }} />
    </Box>
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 1,
        backgroundColor: 'rgba(0, 61, 79, 0.03)',
        border: `1px solid ${colors.border.light}`,
      }}
    >
      <Skeleton variant="text" width="100%" height={18} sx={{ mb: 1, bgcolor: skeletonBg }} />
      <Skeleton variant="text" width="98%" height={18} sx={{ mb: 0.5, bgcolor: skeletonBg }} />
      <Skeleton variant="text" width="85%" height={18} sx={{ bgcolor: skeletonBg }} />
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2, pt: 1.5, borderTop: `1px solid ${colors.border.light}` }}>
      <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: skeletonBg }} />
      <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: skeletonBg }} />
      <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: skeletonBg }} />
    </Box>
  </Paper>
);

export default ClauseCard;
